import { ERole } from '@dlap-ui/core';

export interface ApplicationServiceCatalogItem {
  name: string;
  value: string;
  aliases?: string[];
  icon: string;
  description: string;
  supportedInWorkflow: boolean;
  allowedRoles?: readonly string[];
}

export type ServiceOption = ApplicationServiceCatalogItem;

export const APPLICATION_SERVICE_CATALOG: ApplicationServiceCatalogItem[] = [
  {
    name: 'Deed of Transfer',
    value: 'deed-transfer',
    aliases: ['deed-transfer-bond', 'deed-transfer-ordinary'],
    icon: 'swap',
    description: 'Transfer a registered property from one individual or corporate body to another.',
    // Served by its own step-by-step page (/overview/apply/transfer), not the
    // metadata-driven workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Subdivision/Deduction',
    value: 'deed-subdivision',
    aliases: ['deed-subdivision-bond'],
    icon: 'branches',
    description: 'Transfer part of a registered property. Draft deeds must match approved Surveyor General diagrams or a General Plan.',
    // Served by its own step-by-step page (/overview/apply/subdivision), not
    // the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Certificate of Consolidated Title',
    value: 'deed-consolidation',
    icon: 'cluster',
    description: 'Merge two or more adjacent properties held under separate deeds into a single new property.',
    // Served by its own step-by-step page (/overview/apply/consolidation), not
    // the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Deed of Grant',
    value: 'deed-of-grant',
    aliases: ['deed-of-grant-bond'],
    icon: 'file-protect',
    description: 'Title deed for state land granted directly to an individual or entity. It creates a new title and the first private owner, and applies to grants by the state, not sales between private parties.',
    supportedInWorkflow: true,
    allowedRoles: [ERole.LOCAL_GOVERNMENT_TECHNICIAN],
  },
  {
    name: 'Land Tenure Deed of Grant',
    value: 'land-tenure-deed-of-grant',
    aliases: ['land-tenure-deed-of-grant-bond'],
    icon: 'file-protect',
    description: 'A Deed of Grant for transferring unalienated state land. Draft deeds are lodged by the Minister and handled like a subdivisional transfer.',
    supportedInWorkflow: true,
    allowedRoles: [ERole.MINISTER_OF_LANDS_TECHNICIAN],
  },
  {
    name: 'Deed Donation Transfer',
    value: 'deed-donation-transfer',
    aliases: ['deed-donation-transfer-bond'],
    icon: 'gift',
    description: 'Transfer property by donation, with donor, donee and attachment details.',
    supportedInWorkflow: true,
  },
  {
    name: 'Caveat Registration',
    value: 'caveat-registration',
    icon: 'alert',
    description: 'Register a caveat against a deed, with plaintiffs, defendants and court attachments.',
    supportedInWorkflow: true,
  },
  {
    name: 'Caveat Upliftment',
    value: 'caveat-upliftment',
    icon: 'disconnect',
    description: 'Lift an existing caveat using deed details, office and supporting documents.',
    supportedInWorkflow: true,
  },
  {
    name: 'Sectional Title',
    value: 'sectional-title',
    aliases: ['land-tenure-sectional-title'],
    icon: 'home',
    description: 'Initiated by Notarial Deed to transfer individual units, each with exclusive use rights, share numbers and percentages.',
    // Served by its own step-by-step page (/overview/apply/sectional-title),
    // not the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Land Tenure Transfer',
    value: 'land-tenure-transfer',
    aliases: ['land-tenure-transfer-bond'],
    icon: 'environment',
    description: 'Transfer an already registered farm from one individual or corporate body to another.',
    // Served by its own step-by-step page
    // (/overview/apply/land-tenure-transfer), not the metadata-driven
    // workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Land Tenure Subdivision',
    value: 'land-tenure-subdivision',
    aliases: ['land-tenure-subdivision-bond'],
    icon: 'environment',
    description: 'Like a subdivision, but for land under special tenure. Needs authority permission and creates smaller parcels with separate titles.',
    // Served by its own step-by-step page
    // (/overview/apply/land-tenure-subdivision), not the metadata-driven
    // workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Bond Registration',
    value: 'bond-registration',
    icon: 'safety-certificate',
    description: 'Lets bank users register bonds by submitting the relevant details, which are sent to the law firm\'s system.',
    supportedInWorkflow: true,
  },
  {
    name: 'Bond Cancellation',
    value: 'bond-cancellation',
    icon: 'close-circle',
    description: 'Lets bank users cancel bonds registered to a property by submitting the relevant details, which are sent to the law firm\'s system.',
    supportedInWorkflow: true,
  },
  {
    name: 'Note of Hand',
    value: 'note-of-hand',
    icon: 'wallet',
    description: 'Record note of hand security, with mortgagor and attachment details.',
    supportedInWorkflow: true,
  },
  {
    name: 'Issuance of a Securitized Title Deed',
    value: 'deed-re-issuance',
    aliases: ['deed-re-issuance-bond'],
    icon: 'safety-certificate',
    description: 'Issue a new title deed to replace the old paper format, with the owner\'s information verified and updated.',
    supportedInWorkflow: true,
  },
  {
    name: 'Lease Cancellation',
    value: 'lease-cancellation',
    icon: 'close-circle',
    description: 'Cancel a registered lease before its term ends. The parties lodge consent documents and the registrar updates the records.',
    supportedInWorkflow: true,
  },
  {
    name: 'Estate',
    value: 'estate',
    aliases: ['deed-estate-transfer-bond'],
    icon: 'bank',
    description: 'Transfer property from a deceased person to their heirs.',
    // Served by its own step-by-step page (/overview/apply/estate), not
    // the metadata-driven workflow — so it must not appear in that
    // page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Notarial Deed Section',
    value: 'notarial-deed-section',
    aliases: ['land-tenure-notarial-deed-section'],
    icon: 'file-protect',
    description: 'A notarial deed under Section 27 records an agreement between co-owners of a registered property, giving each owner exclusive use of a defined part of it while ownership stays shared.',
    // Served by its own step-by-step page
    // (/overview/apply/notarial-deed-section), not the metadata-driven
    // workflow — so it must not appear in that page's picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Notarial Deed of Donation and Trust',
    value: 'trust-registration',
    icon: 'gift',
    description: 'Formally record a trust, where a founder transfers assets to trustees to manage for beneficiaries.',
    // Served by its own step-by-step page (/overview/apply/trust-registration),
    // not the metadata-driven workflow — so it must not appear in that page's
    // picker.
    supportedInWorkflow: false,
  },
  {
    name: 'Certificate of Registered Title',
    value: 'certificate-of-registered-title',
    icon: 'safety-certificate',
    description: 'Certificate of Registered Title under Section 41 (Subdivision), issued to the current owners for the portion subdivided from an existing deed.',
    supportedInWorkflow: false,
  },
  {
    name: 'Rectification',
    value: 'rectification',
    icon: 'form',
    description: 'Correct an error on a registered deed, such as a wrong name or ID number, by lodging a consent or affidavit that outlines it. If the property is mortgaged, all bond parties must consent.',
    supportedInWorkflow: true,
  },
];

/**
 * The catalog name for a backend application-type code — e.g.
 * `DEED_RE_ISSUANCE` becomes `Issuance of a Securitized Title Deed`. The codes
 * are the SCREAMING_SNAKE form of the catalog's kebab-case keys, so they are
 * normalised before lookup. A code the catalog does not know is title-cased,
 * which keeps unrecognised drafts readable instead of showing a raw code.
 */
export function resolveServiceName(
  applicationType: string | null | undefined,
  fallback = 'Unknown service',
): string {
  const code = (applicationType ?? '').trim();
  if (!code) {
    return fallback;
  }

  const serviceKey = code.toLowerCase().replace(/_/g, '-');
  const match = APPLICATION_SERVICE_CATALOG.find(
    (item) => item.value === serviceKey || item.aliases?.includes(serviceKey),
  );

  if (match) {
    return match.name;
  }

  return code
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
